import { observable } from 'mobx'
import { addMonths, format, subMonths } from 'date-fns'

class CompletedSurveysStore {
    constructor(store) {
        this.store = store
    }

    productMap = []

    @observable options ={
        industries: [],
        surveyTypes: [],
        productTypes: [],
        respondentTypes: [{ label: 'All', value: 'all' }],
    }

    respondentTypesFilter = []

    selectTypes = [
        { name: 'industries', label: 'Industry:', value: 'industry' },
        { name: 'surveyTypes', label: 'Survey Types:', value: 'surveyType' },
        { name: 'productTypes', label: 'Product Types:', value: 'productType' },
        { name: 'respondentTypes', label: 'Respondent Types:', value: 'respondentType' },
    ]

    @observable completedSurveyRequest = {
        industry: 'all',
        surveyType: 'all',
        productType: 'all',
        respondentType: 'all',
        startDate: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
        endDate: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
    }

    @observable completedSurveyResponse = {
        completed: null,
        started: null,
    }

    getProductMap = async () => {
        this.productMap = await this.store.http.get('/productmap')
        this.setOptions()
    }

    setOptions = () => {
        const prodMap = {
            industries: { all: { label: 'All', value: 'all' } },
            surveyTypes: { all: { label: 'All', value: 'all' } },
            respondentTypes: { },
            productTypes: { all: { label: 'All', value: 'all' } },
        }
        this.productMap.forEach((map) => {
            if (!prodMap.industries[map.industrycode]) {
                prodMap.industries[map.industrycode] = {
                    label: map.industryname,
                    value: map.industrycode,
                }
            }

            if (!prodMap.surveyTypes[map.surveytypecode]) {
                prodMap.surveyTypes[map.surveytypecode] = {
                    label: map.surveytypename,
                    value: map.surveytypecode,
                }
            }

            if (!prodMap.respondentTypes[map.respondenttypecode]) {
                prodMap.respondentTypes[map.respondenttypecode] = {
                    label: `${map.industryname} - ${map.respondenttypename}`,
                    value: map.respondenttypecode,
                    industry: map.industrycode,
                    surveyTypes: new Set([map.surveytypecode]),
                }
            } else {
                prodMap.respondentTypes[map.respondenttypecode]
                    .surveyTypes.add(map.surveytypecode)
            }

            if (!prodMap.productTypes[map.producttypecode]) {
                prodMap.productTypes[map.producttypecode] = {
                    label: map.producttypename,
                    value: map.producttypecode,
                }
            }
        })
        this.options.industries = Object.keys(prodMap.industries)
            .map((key) => (prodMap.industries[key]))
        this.options.surveyTypes = Object.keys(prodMap.surveyTypes)
            .map((key) => (prodMap.surveyTypes[key]))
        this.respondentTypesFilter = Object.keys(prodMap.respondentTypes)
            .map((key) => (prodMap.respondentTypes[key]))
        this.options.productTypes = Object.keys(prodMap.productTypes)
            .map((key) => (prodMap.productTypes[key]))
    }

    setRespondentTypeOptions = () => {
        this.completedSurveyRequest.respondentType = ''
        if (this.completedSurveyRequest.surveyType === 'all' && this.completedSurveyRequest.industry === 'all') {
            this.options.respondentTypes = this.respondentTypesFilter
            this.options.respondentTypes.unshift({ label: 'All', value: 'all' })
            return
        }

        if (this.completedSurveyRequest.surveyType === 'all') {
            this.options.respondentTypes = this.respondentTypesFilter
                .filter((respo) => (respo.industry === this.completedSurveyRequest.industry))
            this.options.respondentTypes.unshift({ label: 'All', value: 'all' })
            return
        }

        if (this.completedSurveyRequest.industry === 'all') {
            this.options.respondentTypes = this.respondentTypesFilter
                .filter((respo) => (respo.surveyTypes
                    && respo.surveyTypes.has(this.completedSurveyRequest.surveyType)))
            this.options.respondentTypes.unshift({ label: 'All', value: 'all' })
            return
        }
        this.options.respondentTypes = this.respondentTypesFilter
            .filter((respo) => (respo.industry === this.completedSurveyRequest.industry
                 && respo.surveyTypes.has(this.completedSurveyRequest.surveyType)))
        this.options.respondentTypes.unshift({ label: 'All', value: 'all' })
    }

    handleCompletedSurveyRequest = (item, selectType) => {
        switch (selectType) {
            case 'industries':
                this.completedSurveyRequest.industry = item.value
                this.setRespondentTypeOptions()
                break
            case 'productTypes':
                this.completedSurveyRequest.productType = item.value
                break
            case 'respondentTypes':
                this.completedSurveyRequest.respondentType = item.value
                break
            case 'surveyTypes':
                this.completedSurveyRequest.surveyType = item.value
                this.setRespondentTypeOptions()
                break
            case 'start':
                this.completedSurveyRequest.startDate = format(new Date(item), 'yyyy-MM-dd')
                break
            case 'end':
                this.completedSurveyRequest.endDate = format(new Date(item), 'yyyy-MM-dd')
                break
            default: this.completedSurveyRequest = { ...this.completedSurveyRequest }
        }
    }

    customizeRequest = (req) => {
        const request = { ...req }

        if (request.productType === 'all') {
            delete request.productType
        }
        if (request.surveyType === 'all') {
            delete request.surveyType
        }
        if (request.respondentType === 'all' || request.respondentType === '') {
            delete request.respondentType
        }
        if (request.industry === 'all') {
            delete request.industry
        }

        return request
    }

    getSystemCompletedSyrveys = async () => {
        const request = this.customizeRequest(this.completedSurveyRequest)
        try {
            this.completedSurveyResponse = await this.store.http.post('/intranet/systemCompletedSurveys', JSON.stringify(request))
        } catch (e) {
            this.store.addNotification(null, `Could not get System Completed Surveys: ${e.message}`, 'error')
        }
    }
}

export default CompletedSurveysStore
